import { Badge, Button, Tooltip, TooltipContent, TooltipTrigger } from "@attmos/flowstack-ui";
import { Copy, X as Cancel, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { ACTIVE_RUN_STATES, type Run, runServedFromCache } from "@/api/runs";
import { useCancelRun, useRetryRun } from "@/api/submission";
import { elapsedLabel, tileCountLabel } from "@/components/runs/run-header-meta";
import { absentTimingLabel } from "@/components/runs/run-timing";
import { RunStateBadge } from "@/components/runs/run-state-badge";

const TERMINAL_FAILURE = new Set([
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
]);

// The active set is `ACTIVE_RUN_STATES` (`api/runs.ts`), imported rather than
// restated. It was a third hand-written copy of the same seven labels (the
// other two were in `api/dag.ts`), and the copies are what let a run be
// "active enough to keep polling" while the Cancel button that gates on the
// same idea has already disappeared. VIS/V1 removed all three.

/**
 * Run-detail page header — mono breadcrumbs, title row (workload, state
 * badge, tile-count chip), the right-aligned mono meta cluster, and the
 * action rail (Cancel / Retry / copy run_id).
 *
 * The cancel + retry mutations live behind useCancelRun/useRetryRun
 * (B2). Buttons gate on the run's current state per the lifecycle:
 *   - Cancel: visible only on active states.
 *   - Retry: visible only on terminal-failure states.
 *
 * CHROME ONLY (Tier-2 chrome 4/4). No state derivation moved: the
 * badge is still `RunStateBadge`, which owns the fitted/failed/partial
 * distinctions, and nothing here can make an error read as "no data"
 * (flowstack#923) because nothing here reads the status except those
 * two pre-existing sets.
 *
 * WHAT THE CLUSTER SHOWS is bounded by what the API sends, not by the
 * mock: `run-header-meta.ts` documents why cost is absent entirely and
 * why the compute figure exists only for a run VCM timed at both ends
 * (flowstack#1270 — before it, for every run at all).
 */
export function RunHeader({ run }: { run: Run }): JSX.Element {
  // A run that carries BOTH names is a Tile run INSIDE a flow — the server
  // stamps the child with its flow (`server/routes/runs.py`: "a flow child
  // names its flow"). Titling it with the flow name put a flow's name beside
  // THIS run's state badge, so a completed `torsion_fit` child rendered as
  // "full_affdo_sequential · completed" while the workspace below it said the
  // flow had failed at `merge_fragments` (flowstack#1223 A). Neither statement
  // was wrong; they were about different subjects and only one of them said so.
  // The title now names what the badge is about, and the flow is a separate
  // chip that says it is the flow.
  const flowOfChild = run.flow_name !== null && run.tile_name !== null ? run.flow_name : null;
  const workload =
    (flowOfChild !== null ? run.tile_name : (run.flow_name ?? run.tile_name)) ?? run.run_id;
  const cancel = useCancelRun();
  const retry = useRetryRun();
  const isActive = ACTIVE_RUN_STATES.has(run.status);
  const isFailed = TERMINAL_FAILURE.has(run.status);
  const elapsed = elapsedLabel(run);
  const servedFromCache = runServedFromCache(run);
  // `null` = render no chip at all; a string = render that. See the comment at
  // the chip for why the two absent cases diverge (#1270).
  const computeLabel =
    elapsed ?? (isActive ? null : absentTimingLabel(run.status, servedFromCache));
  // flowstack#1941: a flow child links to its flow run's own page.
  const flowRunHref = run.flow_run_id ? `/runs/${encodeURIComponent(run.flow_run_id)}` : null;
  const tileCount = tileCountLabel(run);
  const submitted = new Date(run.submitted_at).toLocaleString();

  const copyRunId = (): void => {
    navigator.clipboard
      .writeText(run.run_id)
      .then(() => toast.success("Run ID copied"))
      .catch(() => toast.error("Failed to copy run ID"));
  };

  return (
    <div className="flex flex-col gap-3 border-b border-border-subtle pb-4">
      {/* Breadcrumbs are MONO, per the mock's `.crumbs` and the idiom
          surfaces 2-4 established: an identifier path reads as machine
          text, and the run id inside it already did. `text-xs` is the
          scale step, not a literal — `type-scale.test.ts` fails a second
          copy of any size number in `src`, so the size lives in
          `tailwind.config.js` and the call site names the step. Not
          `.label-micro`: that treatment is uppercase, and upper-casing a
          project slug or a run id would misrepresent an identifier that
          is case-sensitive on the wire. */}
      <div className="flex flex-wrap items-center gap-2 font-mono text-xs text-muted-foreground">
        {run.project_slug && (
          <>
            <Link to={`/projects/${run.project_slug}`} className="hover:text-foreground">
              {run.project_slug}
            </Link>
            <span aria-hidden>·</span>
          </>
        )}
        <Link to="/runs" className="hover:text-foreground">
          Runs
        </Link>
        <span aria-hidden>·</span>
        <span>{run.run_id}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Copy run ID"
              onClick={copyRunId}
            >
              <Copy className="h-3 w-3" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy run ID</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        {/* Title row: workload, state, tile count — one line, as the mock
            has it, rather than the title stacked over a second row. The
            `<h1>` steps DOWN from `text-3xl` (26px) to `text-2xl` (21px):
            `3xl` was chosen against the pre-density scale, and 21px is
            both the mock's `h1` and the step that lets the badge and the
            chip sit beside it without the row wrapping at the widths a
            flow name reaches. Named as the step, never as the number. */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
            {workload}
          </h1>
          <RunStateBadge state={run.status} size="md" />
          {/* WHOSE state that badge is, when the answer is not obvious.
              `flow_name` alone in the title read as a claim about the flow;
              this chip states the containment instead — the badge belongs to
              the Tile run named on the left, and the flow is where it sits. */}
          {flowOfChild !== null && (
            <Badge
              variant="outline"
              className="border-border-subtle bg-surface-2 font-medium text-muted-foreground"
              data-testid="run-header-flow"
            >
              {run.flow_node_name && (
                <>
                  node <span className="ml-1 mr-1 font-mono">{run.flow_node_name}</span>{" "}
                </>
              )}
              in flow{" "}
              {flowRunHref !== null ? (
                <Link
                  to={flowRunHref}
                  className="ml-1 font-mono underline-offset-2 hover:text-foreground hover:underline"
                  title={`Show flow run ${run.flow_run_id} in the Runs list`}
                >
                  {flowOfChild}
                </Link>
              ) : (
                <span className="ml-1 font-mono">{flowOfChild}</span>
              )}
            </Badge>
          )}
          {/* Tile count — the mock's neutral `.chip` beside the state
              chip. `invocations` is the API's own per-Tile rollup, so
              this is a count of rows received, not a guess from the
              flow's shape. `null` where that rollup is the server's
              one-row placeholder for a flow run, in which case no chip
              renders at all — see `run-header-meta.ts`. */}
          {tileCount !== null && (
            <Badge
              variant="outline"
              className="border-border-subtle bg-surface-2 font-medium text-muted-foreground"
            >
              {tileCount}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* The mono meta cluster. The mock's third figure is a cost;
              the run payload carries no cost field, so there is nothing
              to render and nothing to qualify — see `run-header-meta.ts`
              for why a bare figure would repeat flowstack#1089. */}
          {/* Each figure carries an `sr-only` label. The cluster reads by
              position sighted — elapsed, submitted, who — and position is
              exactly what a screen reader does not get, so a bare
              timestamp beside a bare address would be two unlabelled
              strings. The visible row loses the words the old two-line
              header spelled out ("submitted … by …"); the accessible name
              keeps them. */}
          <div className="flex flex-wrap items-center gap-4 font-mono text-xs text-muted-foreground">
            {/* flowstack#1270: this chip has never rendered before — both
                timestamps were hardcoded `None` on the wire — so its wording
                is fixed here at the moment it starts appearing. What it shows
                is VCM's COMPUTE window, which excludes queueing; calling it
                "elapsed" would have it read as the larger wall-time figure the
                DAG lanes and "since submitted" carry. A settled run with no
                window says "not recorded" rather than dropping the chip: an
                absent chip and a run that took no time look identical, and
                that equivalence is the one #1270 exists to break. An ACTIVE
                run drops it — there is genuinely nothing to record yet, and
                the lifecycle rail below already ticks. */}
            {computeLabel !== null &&
              (elapsed === null && servedFromCache ? (
                // flowstack#1943: a cache hit never ran, so it has no window to
                // record; the chip says why instead of "not recorded".
                <span title="Served from the cache: this run reused the outputs of an earlier run with the same cache key and never executed">
                  <span className="sr-only">Run time </span>
                  {computeLabel}
                </span>
              ) : run.timing_source === "lifecycle" ? (
                // flowstack#961: VCM recorded no window, so the server sent
                // the run's own running → settled window, on FlowStack's clock.
                <span title="Run time FlowStack recorded for this run — from entering running to settling, which excludes time spent queued">
                  <span className="sr-only">Run time </span>
                  {computeLabel}
                </span>
              ) : (
                <span title="Compute time VCM recorded for this run — the job's own start-to-finish window, which excludes time spent queued">
                  <span className="sr-only">Compute time </span>
                  {computeLabel}
                </span>
              ))}
            <span>
              <span className="sr-only">Submitted </span>
              <time dateTime={run.submitted_at}>{submitted}</time>
            </span>
            <span>
              <span className="sr-only">Submitted by </span>
              {run.submitted_by}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  cancel.mutate(run.run_id, {
                    onSuccess: (data) => toast.success(`Run cancelled (${data.state})`),
                    onError: (err) => toast.error(`Cancel failed: ${(err as Error).message}`),
                  });
                }}
                disabled={cancel.isPending}
              >
                <Cancel className="h-3.5 w-3.5" aria-hidden />
                Cancel
              </Button>
            )}
            {isFailed && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  retry.mutate(
                    { runId: run.run_id, projectSlug: run.project_slug },
                    {
                      onSuccess: (data) => toast.success(`Retry queued (${data.run_id})`),
                      onError: (err) => toast.error(`Retry failed: ${(err as Error).message}`),
                    }
                  );
                }}
                disabled={retry.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
